import React, {memo, useCallback, useMemo, useState, useEffect} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {CustomizedRange} from "../../../../components/CustomizedRange/RangeComponent"
import "./index.scss"
import {useDispatch, useSelector} from "react-redux"
import {useRPC} from "../../../../hooks/rpc"
import {useMessage} from "../../../../hooks/message"
import {useLocalization} from "../../../../hooks/localization"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../../../components/Select"
import iconScreen from "../../../../assets/images/icons/screen-brightness.png"
import iconScreenSleep from "../../../../assets/images/icons/screen-brightness-sleep.png"
import iconScreenSleepTime from "../../../../assets/images/icons/screen-sleep-time.png"
import {setEntityListAction} from "../../../../store/control/actions"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import { useHistory, useParams } from "react-router-dom"
import { GatewayCommand } from "../../../../types/control"
import { useQuestion } from "../../../../hooks/question"
import { useDebounce } from "../../../../hooks/debounce"

export const ScreenEditScreen = memo(function ScreenEditScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    // @ts-ignore
    const {id} = useParams()
    const dispatch = useDispatch()
    const {failure} = useMessage()
    const question = useQuestion()
    const history = useHistory();
    const screen = useSelector(state => state.control.screenStateList)
    const config = useSelector(state => state.control.config);
    const gatewayList = useSelector(state => state.control.gatewayList)
    const selectedGateway = useMemo(() => gatewayList.find(g => g.id === id), [gatewayList, id])

    const saverTimeList = useMemo<SelectItem<number>[]>(
        () => [
            {
                value: 1,
                label: t("customizations.one_minute")
            },
            {
                value: 5,
                label: t("customizations.five_minutes")
            },
            {
                value: 30,
                label: t("customizations.thirty_minutes")
            },
            {
                value: 60,
                label: t("customizations.one_hour")
            }
        ],
        [t]
    )

    const optionWallpaper = useMemo<SelectItem<string>[]>(
        () => [
            {
                value: "analog-clock",
                label: t("customizations.analog")
            },
            {
                value: "digital-clock",
                label: t("customizations.digital")
            },
            {
                value: "slider",
                label: t("customizations.slide")
            }
        ],
        [t]
    )

    /*const optionScreenRotation = useMemo<SelectItem<string>[]>(
        () => [
            {
                value: "vertical",
                label: "Yatay"
            },
            {
                value: "horizontal",
                label: "Dikey"
            }
        ],
        []
    )*/

    const [selectScreenTime, setSelectScreenTime] = useState<SelectItem<number>>(
        screen.screen_lock_timing_minute == null
            ? saverTimeList[1]
            : saverTimeList.find(timeOption => timeOption.value === screen.screen_lock_timing_minute)
    )

    const [selectScreenType, setSelectScreenType] = useState<SelectItem<string>>(
        optionWallpaper.find(screenType => screenType.value === screen.screen_saver_type)
    )

    /*const [selectRotationType, setSelectRotationType] = useState<SelectItem<string>>(
        optionScreenRotation.find(rotateOption => rotateOption.value === screen.screen_rotate)
    )*/

    const [selectScreenSaverTime, setSelectScreenSaverTime] = useState<SelectItem<number>>(() =>
        saverTimeList.find(timeOption => timeOption.value === screen.screen_saver_timing_minute)
    )

    const [pinCode, setPinCode] = useState(screen.screen_lock_status || false)
    const [screenSaver, setScreenSaver] = useState(screen.screen_saver_status || false)

    const [indicatorStatus, setIndicatorStatus] = useState(config.indicator_status || false)

    const onBackPressed = useCallback(() => {
        history.push("/settings/custom-menu")
    }, [history])

    const updateScreenBrightness = useDebounce((value: number) => {
        rpc.updateScreenBrightness(value)
            .then(() =>
                dispatch(
                    setEntityListAction(
                        {
                            ...screen,
                            screen_standard_brightness: value
                        },
                        "screenState"
                    )
                )
            )
            .catch(failure)
    }, 500)

    const updateScreenSleepBrightness = useDebounce((value: number) => {
        rpc.updateScreenSleepBrightness(value)
            .then(() =>
                dispatch(
                    setEntityListAction(
                        {
                            ...screen,
                            screen_saver_active_brightness: value
                        },
                        "screenState"
                    )
                )
            )
            .catch(failure)
    }, 500)

    const updateScreenSleepTime = useDebounce((value: number) => {
        rpc.updateScreenSleepTime(value)
            .then(() =>
                dispatch(
                    setEntityListAction(
                        {
                            ...screen,
                            screen_sleep_second: value
                        },
                        "screenState"
                    )
                )
            )
            .catch(failure)
    }, 500)
    const onScreenBrightnessChange = useCallback((value: number) => updateScreenBrightness(value), [updateScreenBrightness])
    const onScreenSleepBrightnessChange = useCallback((value: number) => updateScreenSleepBrightness(value), [updateScreenSleepBrightness])
    const onScreenSleepTimeChange = useCallback((value: number) => updateScreenSleepTime(value), [updateScreenSleepTime])

    const onChangeIndicatorStatus = useCallback(() => {
        let newStatus = indicatorStatus
        if (newStatus) {
            newStatus = false
        } else {
            newStatus = true
        }
        setIndicatorStatus(newStatus)
        rpc.updateIndicatorStatus(newStatus)
    }, [rpc, indicatorStatus])


    const onScreenSaverStateChange = useCallback(() => {
        setScreenSaver(!screenSaver)
    }, [screenSaver])

    const onScreenPinCodeStateChange = useCallback(() => {
        setPinCode(!pinCode)
    }, [pinCode])

    const onSaveChanges = useCallback(() => {
        question(t('customizations.do_you_confirm_the_screen_changes'), ()=> {
        rpc.updateScreenPinCodeTime(selectScreenTime?.value)
           .then(() => dispatch(setEntityListAction({...screen, screen_lock_timing_minute: selectScreenTime?.value}, "screenState")))
           .catch(failure)
        rpc.updateScreenSaverType(selectScreenType?.value)
           .then(() => dispatch(setEntityListAction({...screen, screen_saver_type: selectScreenType?.value}, "screenState")))
           .catch(failure)
        rpc.updateScreenSaverTime(selectScreenSaverTime?.value)
           .then(() => dispatch(setEntityListAction({...screen, screen_saver_timing_minute: selectScreenSaverTime?.value}, "screenState")))
           .catch(failure)
        rpc.updateScreenPinCodeState(pinCode)
           .then(() => dispatch(setEntityListAction({...screen, screen_lock_status: pinCode}, "screenState")))
           .catch(failure)
        rpc.updateScreenSaverState(screenSaver)
           .then(() => dispatch(setEntityListAction({...screen, screen_saver_status: screenSaver}, "screenState")))
           .catch(failure)
        rpc.manageGateway(selectedGateway.id, GatewayCommand.RestartWorker).then(() => onBackPressed())
        })
    }, [
        t,
        screen,
        dispatch,
        failure,
        question,
        rpc,
        onBackPressed,
        selectScreenTime?.value,
        selectScreenType?.value,
        selectScreenSaverTime?.value,
        pinCode,
        screenSaver,
        selectedGateway.id
    ])

    const buttons = useMemo(()=> [
        {
            text: t('apply'),
            onClick: onSaveChanges,
            className: 'green',
        },
    ], [onSaveChanges, t])

    return (
        <PasswordContainer requiredRole="default" isFullscreen={true}>
            <SettingsPageContainer buttons={buttons} className="service-edit" backUrl={"/settings/custom-menu"}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingCheckBoxComponent
                            label={t("customizations.indicator_status")}
                            set={onChangeIndicatorStatus}
                            value={indicatorStatus}
                        />
                        <SettingCheckBoxComponent
                            label={t("customizations.screen_saver")}
                            set={onScreenSaverStateChange}
                            value={screenSaver}
                        />
                        <SettingSelectComponent
                            label={t("customizations.screen_saver_timing")}
                            options={saverTimeList}
                            value={selectScreenSaverTime}
                            onChange={setSelectScreenSaverTime}
                        />
                        <SettingSelectComponent
                            label={t("customizations.screen_saver_type")}
                            options={optionWallpaper}
                            value={selectScreenType}
                            onChange={setSelectScreenType}
                        />
                        {/*<SettingSelectComponent
                            label={"Ekran Yönü"}
                            options={optionScreenRotation}
                            value={selectRotationType}
                            onChange={onRotateTypeChange}
                        />*/}
                        <SettingCheckBoxComponent
                            label={t("customizations.screen_lock")}
                            set={onScreenPinCodeStateChange}
                            value={pinCode}
                        />
                        <SettingSelectComponent
                            label={t("customizations.lock_timing")}
                            options={saverTimeList}
                            value={selectScreenTime}
                            onChange={setSelectScreenTime}
                        />
                    </div>
                    <div className="content-wrapper">
                        <CustomizedRange
                            columnProps={{min: 12}}
                            name={t("customizations.screen_brightness")}
                            value={screen.screen_standard_brightness}
                            onChange={onScreenBrightnessChange}
                            activeImage={iconScreen}
                            passiveImage={iconScreen}
                            minValue={10}
                            maxValue={100}
                            stepValue={5}
                            valueType={""}
                        />
                        <CustomizedRange
                            columnProps={{min: 12}}
                            name={t("customizations.screen_sleep_brightness")}
                            value={screen.screen_saver_active_brightness}
                            onChange={onScreenSleepBrightnessChange}
                            activeImage={iconScreenSleep}
                            passiveImage={iconScreenSleep}
                            minValue={0}
                            maxValue={100}
                            stepValue={5}
                            valueType={""}
                        />
                        <CustomizedRange
                            columnProps={{min: 12}}
                            name={t("customizations.screen_sleep_timing")}
                            value={screen.screen_sleep_second}
                            onChange={onScreenSleepTimeChange}
                            activeImage={iconScreenSleepTime}
                            passiveImage={iconScreenSleepTime}
                            minValue={5}
                            maxValue={60}
                            stepValue={5}
                            valueType={"s"}
                        />
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
